import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import StatisticsChartComponent from "./StatisticsChartComponent";
import StatisticsKeyValuesComponentCollection from "./StatisticsKeyValuesComponentCollection";
import { buildCollectionInsights } from "../utils/collectionInsights";
import {
  getPackmatPerformance,
  getStatisticsCollections,
} from "../../../services/container";

const StatisticsCollection = ({
  periodHistoryStart,
  periodHistoryStop,
  periodHistoryStartPrevious,
  periodHistoryStopPrevious,
  enablePreviousPeriod,
  keywordFilter,
  numberOfContainers,
  numberOfSite,
  handleValidate,
  setHandleValidate,
  onAnomalyChange,
  chartCaptureRefs,
  fillingCaptureMode = null,
}) => {
  const [statisticsCollectionCurrent, setStatisticsCollectionCurrent] =
    useState({});
  const [statisticsCollectionPrevious, setStatisticsCollectionPrevious] =
    useState({});
  const [collectionWasteGenerated, setCollectionWasteGenerated] = useState({});
  const [packmatPerformance, setPackmatPerformance] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(
    () => {
      setHandleValidate(true);
    },
    // eslint-disable-next-line
    [],
  );

  const fetchData = () => {
    setIsLoading(true);

    const keywordValue = keywordFilter?.value ?? [];
    const previousCollectionsRequest =
      enablePreviousPeriod &&
      periodHistoryStartPrevious &&
      periodHistoryStopPrevious
        ? getStatisticsCollections({
            periodStart: periodHistoryStartPrevious,
            periodStop: periodHistoryStopPrevious,
            keywordFilter: JSON.stringify(keywordValue),
          })
        : Promise.resolve({
            status: 200,
            data: { countCollections: 0, dataResult: {}, dataByContainer: {} },
          });

    axios
      .all([
        getStatisticsCollections({
          periodStart: periodHistoryStart,
          periodStop: periodHistoryStop,
          keywordFilter: JSON.stringify(keywordValue),
        }),
        previousCollectionsRequest,
        getPackmatPerformance({
          periodStart: periodHistoryStart,
          periodStop: periodHistoryStop,
          keywordFilter: JSON.stringify(keywordValue),
        }),
      ])
      .then(
        axios.spread(
          (
            responseStatisticsCollections,
            responseStatisticsCollectionsPrevious,
            responsePackmatPerformance,
          ) => {
            if (
              responseStatisticsCollections.status === 200 &&
              responseStatisticsCollectionsPrevious.status === 200 &&
              responsePackmatPerformance.status === 200
            ) {
              setPackmatPerformance(responsePackmatPerformance.data);
              setStatisticsCollectionCurrent(
                responseStatisticsCollections.data,
              );
              setStatisticsCollectionPrevious(
                responseStatisticsCollectionsPrevious.data,
              );
              setCollectionWasteGenerated(responseStatisticsCollections.data);

              setIsLoading(false);
              setHandleValidate(false);
            }
          },
        ),
      );
  };

  useEffect(
    () => {
      if (handleValidate) {
        fetchData();
      }
    },
    // eslint-disable-next-line
    [handleValidate],
  );

  const insights = useMemo(
    () => buildCollectionInsights(statisticsCollectionCurrent),
    [statisticsCollectionCurrent],
  );

  useEffect(() => {
    if (!onAnomalyChange) return;
    onAnomalyChange(isLoading ? null : insights.anomaly);
  }, [isLoading, insights, onAnomalyChange]);

  useEffect(() => {
    return () => onAnomalyChange?.(null);
  }, [onAnomalyChange]);

  return (
    <div className="text-blue w-full flex flex-col gap-6">
      <StatisticsKeyValuesComponentCollection
        periodHistoryStart={periodHistoryStart}
        periodHistoryStop={periodHistoryStop}
        periodHistoryStartPrevious={periodHistoryStartPrevious}
        periodHistoryStopPrevious={periodHistoryStopPrevious}
        enablePreviousPeriod={enablePreviousPeriod}
        numberOfContainers={numberOfContainers}
        numberOfSite={numberOfSite}
        statisticsCollectionCurrent={statisticsCollectionCurrent}
        statisticsCollectionPrevious={statisticsCollectionPrevious}
        collectionWasteGenerated={collectionWasteGenerated}
        isLoading={isLoading}
      />

      <StatisticsChartComponent
        statisticsCollectionCurrent={statisticsCollectionCurrent}
        statisticsCollectionPrevious={statisticsCollectionPrevious}
        packmatPerformance={packmatPerformance}
        recommendations={insights.recommendations}
        isLoading={isLoading}
        chartCaptureRefs={chartCaptureRefs}
        fillingCaptureMode={fillingCaptureMode}
      />
    </div>
  );
};

export default StatisticsCollection;
